import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAppStoreIos, faGooglePlay } from "@fortawesome/free-brands-svg-icons";
import iconlogo from "../img/zooE-ticket-icon.png"; // ปรับ path ตามโปรเจกต์

/**
 * AppDownloadBanner — Fully Responsive
 * ─────────────────────────────────────
 * มี 3 โหมดตามขนาดหน้าจอ:
 *  • Desktop  (≥ 640px) : banner แนวนอน ลอยด้านล่างกลางจอ
 *  • Mobile   (< 640px) : banner แนวตั้ง compact ติดด้านล่างเต็มความกว้าง
 *  • Mini     (< 360px) : ซ่อน sub text เพื่อประหยัดพื้นที่
 */
function AppDownloadBanner() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [screenW, setScreenW] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1500);
    const onResize = () => setScreenW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  if (dismissed || !visible) return null;

  const isMobile = screenW < 640;
  const isMini = screenW < 360;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Prompt:wght@600;700;800&family=Sarabun:wght@400;500&display=swap');

        @keyframes zb-slideUp {
          from { transform: translateX(-50%) translateY(80px); opacity: 0; }
          to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
        }
        @keyframes zb-slideUpMobile {
          from { transform: translateY(80px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes zb-ripple {
          0%   { transform: scale(1);   opacity: .45; }
          100% { transform: scale(2.4); opacity: 0;   }
        }
        @keyframes zb-float {
          0%,100% { transform: translateY(0px); }
          50%     { transform: translateY(-5px); }
        }
        @keyframes zb-glow {
          0%,100% { opacity: .12; }
          50%     { opacity: .24; }
        }

        /* ── Wrapper ── */
        .zb-wrap {
          position: fixed;
          z-index: 9999;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          width: min(700px, calc(100vw - 24px));
          animation: zb-slideUp .5s cubic-bezier(.22,1,.36,1) forwards;
        }
        @media (max-width: 639px) {
          .zb-wrap {
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            transform: none;
            animation-name: zb-slideUpMobile;
          }
        }

        /* ── Card ── */
        .zb-card {
          background: linear-gradient(135deg, #12291a 0%, #1e4a2c 55%, #0f2318 100%);
          border: 1px solid rgba(255,255,255,.09);
          box-shadow: 0 24px 64px rgba(12,40,22,.55), 0 4px 16px rgba(0,0,0,.3);
          position: relative;
          overflow: hidden;
          border-radius: 22px;
          padding: 16px 20px 16px 16px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        @media (max-width: 639px) {
          .zb-card {
            border-radius: 20px 20px 0 0;
            padding: 14px 16px 20px;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
        }

        /* Animated glow blob */
        .zb-card::before {
          content: '';
          position: absolute;
          width: 260px; height: 260px;
          background: radial-gradient(circle, rgba(52,200,90,.18) 0%, transparent 70%);
          top: -80px; right: 60px;
          border-radius: 50%;
          pointer-events: none;
          animation: zb-glow 4s ease-in-out infinite;
        }

        /* ── Top row (mobile only) ── */
        .zb-top-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        /* ── App icon ── */
        .zb-icon-wrap {
          flex-shrink: 0;
          position: relative;
          width: 52px; height: 52px;
        }
        .zb-icon-wrap img {
          width: 52px; height: 52px;
          border-radius: 14px;
          display: block;
          position: relative; z-index: 1;
          animation: zb-float 3s ease-in-out infinite;
        }
        .zb-ripple {
          position: absolute;
          inset: -10px;
          border-radius: 24px;
          border: 2px solid rgba(52,200,90,.28);
          animation: zb-ripple 2.6s ease-out infinite;
          pointer-events: none;
        }
        .zb-ripple-2 { animation-delay: 1.3s; }

        /* ── Text ── */
        .zb-text { flex: 1; min-width: 0; }
        .zb-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: rgba(52,200,90,.15);
          border: 1px solid rgba(52,200,90,.28);
          border-radius: 100px;
          padding: 1px 9px;
          font-size: 10px;
          color: #7de89d;
          font-weight: 700;
          letter-spacing: .07em;
          margin-bottom: 3px;
        }
        .zb-title {
          font-weight: 800;
          color: #fff;
          line-height: 1.2;
          margin: 0 0 2px;
          font-size: 15px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .zb-sub {
          font-size: 12px;
          color: rgba(255,255,255,.5);
          line-height: 1.45;
          margin: 0;
        }

        /* ── Download buttons ── */
        .zb-btns {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }
        @media (max-width: 639px) {
          .zb-btns {
            width: 100%;
          }
          .zb-btns a {
            flex: 1;
            justify-content: center;
          }
        }
        @media (max-width: 359px) {
          .zb-btns { flex-direction: column; }
        }

        .zb-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          padding: 10px 16px;
          border-radius: 14px;
          text-decoration: none;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
          transition: all .22s ease;
          -webkit-tap-highlight-color: transparent;
          cursor: pointer;
        }
        .zb-btn:active { transform: scale(.95) !important; }

        .zb-google {
          background: #34a853;
          color: #fff;
          box-shadow: 0 4px 16px rgba(52,168,83,.4);
        }
        .zb-google:hover {
          background: #2b9246;
          box-shadow: 0 8px 24px rgba(52,168,83,.55);
          transform: translateY(-2px);
          color: #fff;
        }

        .zb-apple {
          background: rgba(255,255,255,.1);
          color: #fff;
          border: 1px solid rgba(255,255,255,.2);
        }
        .zb-apple:hover {
          background: rgba(255,255,255,.18);
          transform: translateY(-2px);
          color: #fff;
        }

        .zb-btn-label {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
          text-align: left;
        }
        .zb-btn-label .zb-btn-sub {
          font-size: 9px;
          font-weight: 400;
          opacity: .7;
        }
        .zb-btn-label .zb-btn-name {
          font-size: 13px;
        }

        /* ── Close button ── */
        .zb-close {
          position: absolute;
          top: 10px; right: 10px;
          background: rgba(255,255,255,.1);
          border: none;
          border-radius: 50%;
          width: 28px; height: 28px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          color: rgba(255,255,255,.55);
          font-size: 12px;
          transition: all .2s;
          z-index: 3;
          padding: 0;
          line-height: 1;
          -webkit-tap-highlight-color: transparent;
        }
        .zb-close:hover { background: rgba(255,255,255,.2); color: #fff; }
        .zb-close:active { transform: scale(.88); }
      `}</style>

      <div className="zb-wrap">
        <div className="zb-card">

          <button className="zb-close" onClick={() => setDismissed(true)} aria-label="ปิด">
            ✕
          </button>

          {isMobile ? (
            /* ──── Mobile layout ──── */
            <>
              <div className="zb-top-row">
                {/* Icon */}
                <div className="zb-icon-wrap">
                  <div className="zb-ripple" />
                  <div className="zb-ripple zb-ripple-2" />
                  <img src={iconlogo} alt="Zoo E-Ticket" />
                </div>
                {/* Text */}
                <div className="zb-text">
                  {!isMini && <div className="zb-badge">🐾 แอปพลิเคชัน</div>}
                  <p className="zb-title">Zoo E-Ticket Application</p>
                  {!isMini && (
                    <p className="zb-sub">ซื้อบัตร รวมทุกสวนสัตว์ · iOS & Android</p>
                  )}
                </div>
              </div>
              {/* Buttons full-width row */}
              <div className="zb-btns">
                <a
                  href="https://play.google.com/store/apps/details?id=zoo.ticket.eticket&hl=th&pli=1"
                  target="_blank" rel="noreferrer"
                  className="zb-btn zb-google"
                >
                  <FontAwesomeIcon icon={faGooglePlay} style={{ fontSize: 16 }} />
                  <span className="zb-btn-label">
                    <span className="zb-btn-sub">ดาวน์โหลดบน</span>
                    <span className="zb-btn-name">Google Play</span>
                  </span>
                </a>
                <a
                  href="https://apps.apple.com/th/app/zooe-ticket/id6740426570?l=th"
                  target="_blank" rel="noreferrer"
                  className="zb-btn zb-apple"
                >
                  <FontAwesomeIcon icon={faAppStoreIos} style={{ fontSize: 16 }} />
                  <span className="zb-btn-label">
                    <span className="zb-btn-sub">ดาวน์โหลดบน</span>
                    <span className="zb-btn-name">App Store</span>
                  </span>
                </a>
              </div>
            </>
          ) : (
            /* ──── Desktop layout ──── */
            <>
              <div className="zb-icon-wrap">
                <div className="zb-ripple" />
                <div className="zb-ripple zb-ripple-2" />
                <img src={iconlogo} alt="Zoo E-Ticket" />
              </div>
              <div className="zb-text">
                <div className="zb-badge">🐾 แอปพลิเคชัน</div>
                <p className="zb-title">Zoo E-Ticket Application</p>
                <p className="zb-sub">ซื้อบัตรออนไลน์ รวมทุกสวนสัตว์ · iOS & Android</p>
              </div>
              <div className="zb-btns">
                <a
                  href="https://play.google.com/store/apps/details?id=zoo.ticket.eticket&hl=th&pli=1"
                  target="_blank" rel="noreferrer"
                  className="zb-btn zb-google"
                >
                  <FontAwesomeIcon icon={faGooglePlay} style={{ fontSize: 16 }} />
                  <span className="zb-btn-label">
                    <span className="zb-btn-sub">ดาวน์โหลดบน</span>
                    <span className="zb-btn-name">Google Play</span>
                  </span>
                </a>
                <a
                  href="https://apps.apple.com/th/app/zooe-ticket/id6740426570?l=th"
                  target="_blank" rel="noreferrer"
                  className="zb-btn zb-apple"
                >
                  <FontAwesomeIcon icon={faAppStoreIos} style={{ fontSize: 16 }} />
                  <span className="zb-btn-label">
                    <span className="zb-btn-sub">ดาวน์โหลดบน</span>
                    <span className="zb-btn-name">App Store</span>
                  </span>
                </a>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}

export default AppDownloadBanner;